import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import {
  Mail, Plus, Trash2, Edit2, X, Save, Check,
  Send, Settings, AlertTriangle, RefreshCw, Bell, Package,
} from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_FORM = { email: '', name: '', notify_damage: true, notify_packing: true };

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      role="switch"
      aria-checked={value}
      style={{
        width: 44, height: 24, borderRadius: 12,
        border: 'none', padding: 0, cursor: 'pointer',
        background: value ? 'var(--green)' : 'var(--border)',
        position: 'relative', transition: 'background 250ms', flexShrink: 0,
      }}>
      <div style={{
        position: 'absolute', top: 2,
        left: value ? 22 : 2,
        width: 20, height: 20,
        background: '#fff', borderRadius: '50%',
        transition: 'left 250ms',
        boxShadow: '0 1px 3px rgba(0,0,0,.25)',
      }} />
    </button>
  );
}

function Modal({ title, onClose, children, footer }) {
  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,.5)', backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'var(--bg-card)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:560, maxHeight:'92dvh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ width:36,height:4,background:'var(--border)',borderRadius:2,margin:'10px auto 0',flexShrink:0 }}/>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',borderBottom:'1px solid var(--border)',flexShrink:0 }}>
          <span style={{ fontWeight:700,fontSize:'1rem' }}>{title}</span>
          <button onClick={onClose} style={{ display:'flex',alignItems:'center',padding:7,border:'none',background:'var(--bg)',cursor:'pointer',borderRadius:8,color:'var(--text)' }}><X size={16}/></button>
        </div>
        <div style={{ flex:1,overflowY:'auto',padding:'16px 18px' }}>{children}</div>
        {footer && <div style={{ padding:'14px 18px',borderTop:'1px solid var(--border)',display:'flex',gap:10,flexShrink:0 }}>{footer}</div>}
      </div>
    </div>
  );
}

export default function EmailVerwaltung() {
  const { user, can } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'admin' && !can('email_manage')) {
      navigate('/dashboard');
    }
  }, [user, navigate, can]);

  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(false); // false | 'add' | 'edit' | 'smtp' | 'test'
  const [form, setForm]             = useState(EMPTY_FORM);
  const [editing, setEditing]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [smtp, setSmtp]             = useState({});
  const [testEmail, setTestEmail]   = useState('');
  const [sending, setSending]       = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/email/recipients')
      .then(r => setRecipients(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSmtp = () => {
    api.get('/email/settings')
      .then(r => setSmtp(r.data))
      .catch(() => {});
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModal('add');
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      email:          r.email,
      name:           r.name || '',
      notify_damage:  !!r.notify_damage,
      notify_packing: !!r.notify_packing,
    });
    setModal('edit');
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.email || !form.email.includes('@')) {
      toast.error('Bitte gültige E-Mail-Adresse eingeben');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/email/recipients/${editing.id}`, form);
        toast.success('Aktualisiert');
      } else {
        await api.post('/email/recipients', form);
        toast.success('Empfänger hinzugefügt');
      }
      setModal(false);
      load();
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler');
    } finally { setSaving(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`${r.email} wirklich löschen?`)) return;
    try {
      await api.delete(`/email/recipients/${r.id}`);
      toast.success('Gelöscht');
      load();
    } catch { toast.error('Fehler'); }
  };

  const toggleActive = async (r) => {
    try {
      await api.put(`/email/recipients/${r.id}`, { ...r, active: !r.active });
      load();
    } catch { toast.error('Fehler'); }
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/email/settings', smtp);
      toast.success('SMTP-Einstellungen gespeichert');
      setModal(false);
    } catch { toast.error('Fehler'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail.includes('@')) { toast.error('Ungültige E-Mail'); return; }
    setSending(true);
    try {
      await api.post('/email/test', { email: testEmail });
      toast.success(`Test-E-Mail gesendet an ${testEmail}`);
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler beim Senden');
    } finally { setSending(false); }
  };

  const activeCount   = recipients.filter(r => r.active).length;
  const damageCount   = recipients.filter(r => r.active && r.notify_damage).length;
  const packingCount  = recipients.filter(r => r.active && r.notify_packing).length;

  return (
    <Layout title="E-Mail Benachrichtigungen">

      {/* Erklärung */}
      <div style={{
        padding:'14px 16px', marginBottom:16,
        background:'var(--blue-bg)', borderRadius:12,
        border:'1px solid rgba(37,99,235,.15)',
        fontSize:'.82rem', color:'var(--blue)', lineHeight:1.7,
      }}>
        <strong>📧 Automatische E-Mail-Benachrichtigungen</strong><br/>
        Eingetragene Empfänger erhalten eine E-Mail wenn eine neue Schadensmeldung oder Packliste angelegt wird.
      </div>

      {/* Stats + Aktionen */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <span style={{ fontSize:'.78rem', color:'var(--text-muted)' }}>
            <strong style={{color:'var(--text)'}}>{activeCount}</strong> aktive Empfänger
          </span>
          {damageCount > 0 && (
            <span style={{ fontSize:'.78rem', color:'var(--red)' }}>
              <AlertTriangle size={12} style={{marginRight:3}}/>
              {damageCount} für Schäden
            </span>
          )}
          {packingCount > 0 && (
            <span style={{ fontSize:'.78rem', color:'var(--blue)' }}>
              <Package size={12} style={{marginRight:3}}/>
              {packingCount} für Packlisten
            </span>
          )}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => { loadSmtp(); setModal('smtp'); }}>
            <Settings size={13}/> SMTP
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setModal('test')}>
            <Send size={13}/> Test
          </button>
          <button className="btn btn-primary btn-sm" onClick={openAdd}>
            <Plus size={13}/> Empfänger
          </button>
        </div>
      </div>

      {/* Empfänger-Liste */}
      {loading ? (
        <div className="loading-box"><div className="spinner spinner-lg"/></div>
      ) : recipients.length === 0 ? (
        <div className="card">
          <div className="empty-box">
            <Mail size={40}/>
            <h3>Noch keine Empfänger</h3>
            <p style={{fontSize:'.82rem',color:'var(--text-muted)'}}>
              Füge E-Mail-Adressen hinzu die bei neuen Meldungen benachrichtigt werden sollen.
            </p>
            <button className="btn btn-primary" style={{marginTop:14}} onClick={openAdd}>
              <Plus size={14}/> Ersten Empfänger hinzufügen
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {recipients.map(r => (
            <div key={r.id} className="card" style={{ padding:'14px 16px', opacity: r.active ? 1 : .6 }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                {/* Avatar */}
                <div style={{
                  width:42, height:42, borderRadius:'50%', flexShrink:0,
                  background: r.active ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : 'var(--border)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color:'#fff', fontSize:'.85rem', fontWeight:800,
                }}>
                  {(r.name || r.email).slice(0,1).toUpperCase()}
                </div>

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:'.9rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {r.name || <span style={{color:'var(--text-muted)'}}>Kein Name</span>}
                  </div>
                  <div style={{ fontSize:'.75rem', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {r.email}
                  </div>
                  {/* Benachrichtigungs-Typen */}
                  <div style={{ display:'flex', gap:5, marginTop:6, flexWrap:'wrap' }}>
                    {r.notify_damage && r.active && (
                      <span style={{ display:'flex', alignItems:'center', gap:3, padding:'2px 7px', borderRadius:999, background:'var(--red-bg)', color:'var(--red)', fontSize:'.65rem', fontWeight:700 }}>
                        <AlertTriangle size={9}/> Schäden
                      </span>
                    )}
                    {r.notify_packing && r.active && (
                      <span style={{ display:'flex', alignItems:'center', gap:3, padding:'2px 7px', borderRadius:999, background:'var(--blue-bg)', color:'var(--blue)', fontSize:'.65rem', fontWeight:700 }}>
                        <Package size={9}/> Packlisten
                      </span>
                    )}
                    {!r.active && (
                      <span style={{ padding:'2px 7px', borderRadius:999, background:'var(--border)', color:'var(--text-muted)', fontSize:'.65rem', fontWeight:700 }}>
                        Deaktiviert
                      </span>
                    )}
                  </div>
                </div>

                {/* Aktionen */}
                <div style={{ display:'flex', gap:6, flexShrink:0, alignItems:'center' }}>
                  <Toggle value={!!r.active} onChange={() => toggleActive(r)} />
                  <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openEdit(r)}>
                    <Edit2 size={14}/>
                  </button>
                  <button className="btn btn-ghost btn-icon btn-sm" style={{color:'var(--red)'}} onClick={() => del(r)}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <Modal
          title={modal === 'add' ? 'Empfänger hinzufügen' : 'Empfänger bearbeiten'}
          onClose={() => setModal(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" style={{flex:1,justifyContent:'center'}} onClick={() => setModal(false)}>Abbrechen</button>
              <button type="button" className="btn btn-primary" style={{flex:1,justifyContent:'center'}} onClick={save} disabled={saving}>
                {saving ? <><span className="spinner" style={{width:14,height:14,borderWidth:2}}/> Speichern...</> : <><Save size={14}/> Speichern</>}
              </button>
            </>
          }>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div className="f-group">
              <label className="f-label">E-Mail-Adresse *</label>
              <input
                className="f-input"
                type="email"
                value={form.email}
                onChange={e => f('email', e.target.value)}
                placeholder="max.mustermann@example.de"
                autoCapitalize="none"
                autoFocus
                required
              />
            </div>
            <div className="f-group">
              <label className="f-label">Name (optional)</label>
              <input
                className="f-input"
                value={form.name}
                onChange={e => f('name', e.target.value)}
                placeholder="Max Mustermann"
              />
            </div>

            {/* Benachrichtigungs-Typen */}
            <div>
              <div className="f-label" style={{marginBottom:10}}>Benachrichtigen bei</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {[
                  { key:'notify_damage',  icon:<AlertTriangle size={16} color="var(--red)"/>,  label:'Neue Schadensmeldungen', sub:'Sofort wenn ein Schaden gemeldet wird', bg:'var(--red-bg)' },
                  { key:'notify_packing', icon:<Package size={16} color="var(--blue)"/>,       label:'Neue Packlisten',        sub:'Wenn eine neue Packliste erstellt wird', bg:'var(--blue-bg)' },
                ].map(({ key, icon, label, sub, bg }) => (
                  <div
                    key={key}
                    style={{
                      display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
                      borderRadius:10, border:'1.5px solid',
                      borderColor: form[key] ? 'var(--border)' : 'var(--border)',
                      background: form[key] ? bg : 'var(--bg)',
                      cursor:'pointer', transition:'all 200ms',
                    }}
                    onClick={() => f(key, !form[key])}>
                    <div style={{ width:34, height:34, borderRadius:9, background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {icon}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:'.85rem' }}>{label}</div>
                      <div style={{ fontSize:'.72rem', color:'var(--text-muted)', marginTop:1 }}>{sub}</div>
                    </div>
                    <Toggle value={form[key]} onChange={v => f(key, v)} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* SMTP Modal */}
      {modal === 'smtp' && (
        <Modal
          title="SMTP-Einstellungen"
          onClose={() => setModal(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" style={{flex:1,justifyContent:'center'}} onClick={() => setModal(false)}>Abbrechen</button>
              <button type="button" className="btn btn-primary" style={{flex:1,justifyContent:'center'}} onClick={saveSmtp} disabled={saving}>
                {saving ? <><span className="spinner" style={{width:14,height:14,borderWidth:2}}/> Speichern...</> : <><Save size={14}/> Speichern</>}
              </button>
            </>
          }>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ padding:'12px 14px', background:'var(--yellow-bg)', borderRadius:10, border:'1px solid rgba(217,119,6,.2)', fontSize:'.78rem', color:'var(--yellow)', lineHeight:1.6 }}>
              <strong>Empfehlung:</strong> Für zuverlässigen Versand einen SMTP-Relay-Dienst nutzen:<br/>
              <strong>Brevo (ehemals Sendinblue)</strong> — kostenlos bis 300 E-Mails/Tag<br/>
              <strong>SMTP:</strong> smtp-relay.brevo.com | <strong>Port:</strong> 587
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 120px', gap:10 }}>
              <div className="f-group">
                <label className="f-label">SMTP-Host</label>
                <input className="f-input" value={smtp.smtp_host||''} onChange={e=>setSmtp(p=>({...p,smtp_host:e.target.value}))} placeholder="smtp-relay.brevo.com"/>
              </div>
              <div className="f-group">
                <label className="f-label">Port</label>
                <input type="number" className="f-input" value={smtp.smtp_port||'587'} onChange={e=>setSmtp(p=>({...p,smtp_port:e.target.value}))}/>
              </div>
            </div>
            <div className="f-group">
              <label className="f-label">Benutzername / API-Key</label>
              <input className="f-input" value={smtp.smtp_user||''} onChange={e=>setSmtp(p=>({...p,smtp_user:e.target.value}))} placeholder="ihre@email.de oder API-Key" autoCapitalize="none"/>
            </div>
            <div className="f-group">
              <label className="f-label">Passwort / SMTP-Key</label>
              <input type="password" className="f-input" value={smtp.smtp_pass||''} onChange={e=>setSmtp(p=>({...p,smtp_pass:e.target.value}))} placeholder="Passwort oder SMTP-Schlüssel"/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div className="f-group">
                <label className="f-label">Absender-E-Mail</label>
                <input type="email" className="f-input" value={smtp.smtp_from||''} onChange={e=>setSmtp(p=>({...p,smtp_from:e.target.value}))} placeholder="noreply@fuhrparkgerhards.leven.onl" autoCapitalize="none"/>
              </div>
              <div className="f-group">
                <label className="f-label">Absender-Name</label>
                <input className="f-input" value={smtp.smtp_from_name||''} onChange={e=>setSmtp(p=>({...p,smtp_from_name:e.target.value}))} placeholder="FuhrparkGerhards"/>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:'var(--bg)', borderRadius:10, border:'1px solid var(--border)' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:'.85rem' }}>SSL/TLS verwenden</div>
                <div style={{ fontSize:'.72rem', color:'var(--text-muted)', marginTop:1 }}>Für Port 465 aktivieren, für 587 deaktiviert lassen</div>
              </div>
              <Toggle value={smtp.smtp_secure==='1'} onChange={v=>setSmtp(p=>({...p,smtp_secure:v?'1':'0'}))}/>
            </div>
          </div>
        </Modal>
      )}

      {/* Test-E-Mail Modal */}
      {modal === 'test' && (
        <Modal
          title="Test-E-Mail senden"
          onClose={() => setModal(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" style={{flex:1,justifyContent:'center'}} onClick={() => setModal(false)}>Abbrechen</button>
              <button type="button" className="btn btn-primary" style={{flex:1,justifyContent:'center'}} onClick={sendTest} disabled={sending}>
                {sending ? <><span className="spinner" style={{width:14,height:14,borderWidth:2}}/> Sende...</> : <><Send size={14}/> Senden</>}
              </button>
            </>
          }>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ fontSize:'.85rem', color:'var(--text-muted)' }}>
              Sendet eine Test-E-Mail um zu prüfen ob die SMTP-Einstellungen korrekt sind.
            </div>
            <div className="f-group">
              <label className="f-label">Empfänger-Adresse</label>
              <input
                type="email"
                className="f-input"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="test@example.de"
                autoCapitalize="none"
                autoFocus
              />
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
}
